/**
 * The two calls to the standings (server/standingsApi.mjs), in the shape of `wallTransport.ts`:
 * nothing here throws and nothing here logs. A server that is missing, asleep or answering the
 * game's HTML is an ordinary outcome - the Records screen simply has one panel fewer.
 */

import type { FetchLike } from '../cloudSave/types';
import type { Standings, StandingsRecord } from './standings';

const REQUEST_TIMEOUT_MS = 12000;

function timeout(ms: number): AbortSignal | undefined {
  const able = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
  return able ? AbortSignal.timeout(ms) : undefined;
}

/**
 * Rebuilt field by field, exactly as the server does with what a client sends: the panel draws
 * six numbers and a count, so six numbers and a count is all that is ever read out of the answer.
 */
export function asStandings(raw: unknown): Standings | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  // No `saves` means this was not the standings at all - a proxy's error page, or an older server.
  if (typeof r['saves'] !== 'number' || !Number.isFinite(r['saves'])) return null;
  const n = (key: string): number => {
    const value = r[key];
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  };
  return {
    saves: n('saves'), highestRank: n('highestRank'), mostMissions: n('mostMissions'),
    mostMedals: n('mostMedals'), bestStreak: n('bestStreak'),
    rangersLost: n('rangersLost'), operationsWon: n('operationsWon'),
  };
}

export interface StandingsTransport {
  /** The aggregate, or null when the server says nothing: the panel is then not drawn at all. */
  read(): Promise<Standings | null>;
  /** True when the server took this company's figures. */
  file(code: string, record: StandingsRecord): Promise<boolean>;
}

export function createStandingsTransport(fetchLike: FetchLike, origin: string): StandingsTransport {
  const url = `${origin}/api/standings`;

  return {
    async read() {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url, { cache: 'no-store', ...(signal ? { signal } : {}) });
        if (!res.ok) return null;
        return asStandings(await res.json());
      } catch {
        return null;
      }
    },

    async file(code, record) {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${code}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
          cache: 'no-store',
          ...(signal ? { signal } : {}),
        });
        return res.ok;
      } catch {
        return false;
      }
    },
  };
}
